/**
 * User Profile API Client
 *
 * Handles user profile CRUD operations with Cognito authentication.
 */

// API Gateway URL
const API_BASE_URL = 'https://jrgfnay5w2.execute-api.ap-southeast-2.amazonaws.com/v1';

/**
 * Get the authentication token from the user's session.
 */
const getAuthToken = () => {
  const oidcStorage = sessionStorage.getItem('oidc.user:https://cognito-idp.ap-southeast-2.amazonaws.com/ap-southeast-2_3n9j6gDzL:2c6na652k2lkcsaa1ol8m5chp4');
  if (oidcStorage) {
    try {
      const oidcData = JSON.parse(oidcStorage);
      return oidcData.id_token || oidcData.access_token;
    } catch (e) {
      console.error('Error parsing OIDC storage:', e);
    }
  }
  return null;
};

/**
 * Make an authenticated API request to the profile endpoint.
 */
const apiRequest = async (operation, data = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated. Please log in again.');
  }

  const response = await fetch(`${API_BASE_URL}/profile`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      operation,
      data
    })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || `Request failed with status ${response.status}`);
  }

  return result;
};

/**
 * Get the current user's own profile.
 * Available to all authenticated users.
 *
 * Concurrent callers share one request. On a portal page load the navbar, the layout and the
 * page itself each ask for the profile at the same moment, which was four identical round
 * trips; the page cannot render until they finish, so they sat directly on the critical path.
 * Only in-flight calls are shared — once one settles the next call goes to the network again,
 * so this never serves a stale profile.
 */
let inFlightMyProfile = null;

export const getMyProfile = async () => {
  if (inFlightMyProfile) return inFlightMyProfile;

  inFlightMyProfile = apiRequest('getMyProfile')
    .then(result => result.profile)
    .finally(() => { inFlightMyProfile = null; });

  return inFlightMyProfile;
};

/**
 * Update the current user's own profile.
 * Available to all authenticated users.
 *
 * @param {Object} profileData - Profile fields to update
 */
export const updateMyProfile = async (profileData) => {
  const result = await apiRequest('updateMyProfile', profileData);
  return result;
};

/**
 * Get a specific user's profile (admin operation).
 * Available to portal_system_admin, portal_director, portal_manager, portal_admin.
 *
 * @param {string} userId - User's ID in UserPortal table
 */
export const getUserProfile = async (userId) => {
  const result = await apiRequest('getUserProfile', { userId });
  return result.profile;
};

/**
 * Update a specific user's profile (admin operation).
 * Uses hierarchical permission control.
 *
 * @param {string} userId - User's ID in UserPortal table
 * @param {Object} profileData - Profile fields to update
 */
export const updateUserProfile = async (userId, profileData) => {
  const result = await apiRequest('updateUserProfile', { userId, profile: profileData });
  return result;
};

/**
 * Get a presigned URL for avatar upload.
 * Available to all authenticated users.
 *
 * @param {string} fileName - Original file name
 * @param {string} contentType - MIME type of the file
 */
export const getAvatarUploadUrl = async (fileName, contentType) => {
  const result = await apiRequest('getAvatarUploadUrl', { fileName, contentType });
  return {
    uploadUrl: result.uploadUrl,
    avatarUrl: result.avatarUrl,
    key: result.key
  };
};

/**
 * Upload an avatar file to S3 using presigned URL and update profile.
 *
 * @param {File} file - The file to upload
 */
export const uploadAvatar = async (file) => {
  // Get presigned URL
  const { uploadUrl, avatarUrl } = await getAvatarUploadUrl(file.name, file.type);

  // Upload to S3
  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type
    },
    body: file
  });

  if (!uploadResponse.ok) {
    throw new Error('Failed to upload avatar');
  }

  // Update profile with new avatar URL
  await updateMyProfile({ avatarUrl });

  return avatarUrl;
};

/**
 * Upload an avatar for a specific user (admin operation).
 *
 * @param {string} userId - User's ID
 * @param {File} file - The file to upload
 */
export const uploadUserAvatar = async (userId, file) => {
  // Get presigned URL
  const { uploadUrl, avatarUrl } = await getAvatarUploadUrl(file.name, file.type);

  // Upload to S3
  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type
    },
    body: file
  });

  if (!uploadResponse.ok) {
    throw new Error('Failed to upload avatar');
  }

  // Update user's profile with new avatar URL
  await updateUserProfile(userId, { avatarUrl });

  return avatarUrl;
};

const userProfileApi = {
  getMyProfile,
  updateMyProfile,
  getUserProfile,
  updateUserProfile,
  getAvatarUploadUrl,
  uploadAvatar,
  uploadUserAvatar
};

export default userProfileApi;
